// @flow

import React, {type Node} from 'react';
import {connect} from 'react-redux';

import {
  websocketConsumer,
  type websocketConsumerProps,
} from 'app/ws/ws-consumer-hoc';
import type {contentType} from 'app/utils/content-type';
import {renderNullOnError} from 'app/general/renderNullOnError';
import {getGroupByDocument} from 'app/presence/utils';
import {requestIdleCallback} from 'app/utils/browser';
import {getDifference} from 'app/utils/math';

import {
  subscribeForPresence,
  unsubscribeForPresence,
  type CommonPresenceActionParams,
} from 'app/presence/actions';
import type {AdditionalPresenceDataType} from '../types';

type OwnProps = {|
  pageContext?: string,
  stealth?: boolean,
  contentType: contentType,
  additionalData?: AdditionalPresenceDataType,
  objectsIds: Array<number> | Array<string>,
  children?: Node,
|};

type OwnPropsWithWebsocketConsumerProps = {|
  ...websocketConsumerProps,
  ...OwnProps,
|};

type DispatchProps = {|
  subscribeForPresence: AsyncAction<typeof subscribeForPresence, null>,
  unsubscribeForPresence: (CommonPresenceActionParams) => void,
|};

type Props = {|
  ...OwnPropsWithWebsocketConsumerProps,
  ...OwnProps,
  ...DispatchProps,
|};

class BasePresenceSubscriber extends React.Component<Props> {
  getGroups(objectIds: Array<number | string>): Array<string> {
    const {contentType} = this.props;
    return objectIds.map((oid) => getGroupByDocument(contentType, oid));
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const objectIds = this.props.objectsIds;
    const nextObjectIds = nextProps.objectsIds;

    if (objectIds.toString() !== nextObjectIds.toString()) {
      const toSubscribe = getDifference(nextObjectIds, objectIds);
      const toUnsubscribe = getDifference(objectIds, nextObjectIds);

      this.subscribe(toSubscribe);
      this.unsubscribe(toUnsubscribe);
    }
  }

  subscribe(objectsIds: Array<number | string>) {
    if (objectsIds.length > 0) {
      const {
        pageContext,
        stealth,
        sendWebsocketMessage,
        subscribeForPresence,
        additionalData,
      } = this.props;
      const groups = this.getGroups(objectsIds);

      requestIdleCallback(() =>
        subscribeForPresence(
          {
            groups,
            context: pageContext,
            sendWebsocketMessage,
            additionalData,
          },
          {stealth}
        )
      );
    }
  }

  unsubscribe(objectsIds: Array<number | string>) {
    if (objectsIds.length > 0) {
      const {pageContext, sendWebsocketMessage, unsubscribeForPresence} =
        this.props;
      const groups = this.getGroups(objectsIds);
      unsubscribeForPresence({
        groups,
        context: pageContext,
        sendWebsocketMessage,
      });
    }
  }

  UNSAFE_componentWillMount() {
    /* loading a new page is a heavy task so as subscribing for group presence,
        so let's allow React mount the interface before we subscribe for presence */
    this.subscribe(this.props.objectsIds);
  }

  componentWillUnmount() {
    this.unsubscribe(this.props.objectsIds);
  }

  render() {
    return this.props.children != null ? this.props.children : null;
  }
}

const mapDispatchToProps = {
  subscribeForPresence,
  unsubscribeForPresence,
};

export const PresenceSubscriber = renderNullOnError(
  'PresenceSubscriber'
)<OwnProps>(
  websocketConsumer(
    connect<Props, OwnPropsWithWebsocketConsumerProps, _, DispatchProps, _, _>(
      null,
      // $FlowFixMe - redux-thunk messes up with actions return types
      mapDispatchToProps
    )(BasePresenceSubscriber)
  )
);
